import {useLayoutEffect, useRef, type RefObject} from "react";

type Snapshot = {left: number; top: number; width: number; height: number; clone: HTMLElement};
const timing: KeyframeAnimationOptions = {duration: 250, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)"};

/** Animate presentation only: the current turn and clickable DOM always update immediately. */
export function useInitiativeAnimation(ref: RefObject<HTMLDivElement | null>, activeId: string | undefined,
    round: number, privacyKey: string) {
    const previous = useRef<{activeId?: string; round: number; privacyKey: string; items: Map<string, Snapshot>} | null>(null);
    useLayoutEffect(() => {
        const track = ref.current?.querySelector<HTMLOListElement>("ol");
        if (!track) {previous.current = null; return;}
        const nodes = [...track.querySelectorAll<HTMLElement>("[data-combatant-id], [role=separator]")];
        const items = new Map<string, Snapshot>();
        const animations: Animation[] = [];
        const ghosts: HTMLElement[] = [];
        const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        const old = previous.current;
        const changed = old && (old.activeId !== activeId || old.round !== round);
        const animate = changed && old.privacyKey === privacyKey && !motion?.matches;
        const stop = () => {animations.forEach(animation => animation.cancel()); ghosts.forEach(ghost => ghost.remove());};
        for (const node of nodes) {
            const id = node.dataset.initiativeKey ?? node.dataset.combatantId ?? "round-divider";
            const snapshot = {left: node.offsetLeft, top: node.offsetTop, width: node.offsetWidth,
                height: node.offsetHeight, clone: node.cloneNode(true) as HTMLElement};
            items.set(id, snapshot);
            if (!animate || !node.animate) continue;
            const before = old.items.get(id);
            if (node.dataset.combatantId === old.activeId && old.activeId !== activeId) {
                // The old actor reappears at the tail instead of flying across the other portraits.
                animations.push(node.animate([{opacity: 0}, {opacity: 1}], {...timing, duration: 150, delay: 100, fill: "backwards"}));
            } else if (before && snapshot.width && snapshot.height) {
                animations.push(node.animate([
                    {transform: `translate(${before.left - snapshot.left}px, ${before.top - snapshot.top}px) scale(${before.width / snapshot.width}, ${before.height / snapshot.height})`, transformOrigin: "top left"},
                    {transform: "translate(0, 0) scale(1, 1)", transformOrigin: "top left"},
                ], timing));
            } else {
                animations.push(node.animate([{opacity: 0}, {opacity: 1}], timing));
            }
        }
        if (animate && old.activeId && old.activeId !== activeId) {
            const before = old.items.get(`${old.round}:${old.activeId}`) ?? old.items.get(old.activeId);
            if (before?.clone.animate) {
                const ghost = before.clone;
                // A transient, inert visual copy cannot expose stale private state or intercept input.
                ghost.inert = true;
                ghost.setAttribute("aria-hidden", "true");
                ghost.removeAttribute("aria-current");
                ghost.removeAttribute("aria-label");
                ghost.removeAttribute("data-combatant-id");
                ghost.style.cssText = `position:absolute;left:${before.left}px;top:${before.top}px;width:${before.width}px;height:${before.height}px;pointer-events:none;z-index:10001`;
                track.appendChild(ghost);
                ghosts.push(ghost);
                const animation = ghost.animate([{opacity: 1}, {opacity: 0}], {...timing, duration: 120, fill: "forwards"});
                animations.push(animation);
                animation.onfinish = () => ghost.remove();
            }
        }
        previous.current = {activeId, round, privacyKey, items};
        const reduceMotion = () => {if (motion?.matches) stop();};
        motion?.addEventListener("change", reduceMotion);
        return () => {stop(); motion?.removeEventListener("change", reduceMotion);};
    });
}
